import { useEffect } from "react";
import { BOARD } from "@grundbesitz/shared";
import type { RoomView } from "@grundbesitz/shared";
import { formatMoney } from "../../lib/format";
import { availability, mortgageDecisionCosts, mortgageDecisionsOf } from "../../lib/gameView";
import { GroupSwatch } from "../board/GroupBadge";
import { Button } from "../ui/Button";
import { Sheet } from "../ui/Sheet";
import { useAct } from "./hooks";
import { useUiStore } from "./uiStore";

/**
 * After taking over a mortgaged property (trade or bankruptcy) the new owner has to decide at once:
 * lift the mortgage now or keep it and pay the interest. Everything else waits for this decision.
 */
export function MortgageDecisionDialog({ room }: { room: RoomView }) {
  const game = room.game;
  const selfId = room.you.playerId;
  const decisions = game ? mortgageDecisionsOf(game, selfId) : [];
  const hidden = useUiStore((s) => s.mortgageDialogHidden);
  const setHidden = useUiStore((s) => s.setMortgageDialogHidden);
  const { run, busy } = useAct();
  const count = decisions.length;

  useEffect(() => {
    if (count === 0) setHidden(false);
  }, [count, setHidden]);

  if (!game || count === 0) return null;
  return (
    <Sheet open={!hidden} onClose={() => setHidden(true)} title="Hypothek übernommen">
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          {count === 1 ? "Dieses Grundstück ist belastet." : "Diese Grundstücke sind belastet."} Entscheide sofort, bis dahin ruht das Spiel.
        </p>
        <ul className="space-y-4">
          {decisions.map((d) => {
            const f = BOARD[d.field];
            const { unmortgage, interest } = mortgageDecisionCosts(d.field);
            const lift = availability(game, selfId, { type: "MORTGAGE_DECISION", field: d.field, choice: "UNMORTGAGE" });
            return (
              <li key={d.field} className="rounded-xl border border-line p-3">
                <p className="flex items-center gap-2 font-semibold">
                  {f.kind === "STREET" && <GroupSwatch group={f.group} size={18} />}
                  {f.name}
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <Button
                    variant="primary"
                    className="min-h-12"
                    disabled={busy || !lift.ok}
                    title={lift.ok ? undefined : lift.reason}
                    onClick={() => void run({ type: "MORTGAGE_DECISION", field: d.field, choice: "UNMORTGAGE" })}
                  >
                    Hypothek auflösen ({formatMoney(unmortgage)})
                  </Button>
                  <Button
                    className="min-h-12"
                    disabled={busy}
                    onClick={() => void run({ type: "MORTGAGE_DECISION", field: d.field, choice: "KEEP" })}
                  >
                    Belassen ({formatMoney(interest)} Zinsen jetzt)
                  </Button>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-ink-2">
                  Auflösen bringt die Miete zurück. Belassen kostet jetzt nur die Zinsen, das Grundstück bleibt ohne Miete, und eine spätere Auflösung kostet erneut {formatMoney(unmortgage)}.
                </p>
                {!lift.ok && <p className="mt-1 text-xs text-danger">{lift.reason}</p>}
              </li>
            );
          })}
        </ul>
      </div>
    </Sheet>
  );
}
